import { DecimalPipe } from "@angular/common";
import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import moment from "moment";
import { isMoment, Moment } from "moment";
import { languages } from '../shared/lists/languages';
import { BranchActivity, IRawNcmRange } from "@app/interface/operation";
import { APPORTIONMENT_CODE } from "@app/shared/enums/apportionment";
import { IPerson } from "@app/interface/person";

export class Utilities {
  static language: string = 'pt';
  static defaultSeparator = '%';

  /** One day, in milisseconds */
  static MS_DAY = 24 * 60 * 60 * 1000;

  static withSeededPerson<T extends IPerson>(people: T[], seed?: T): T[] {
    if (!seed?.id) {
      return people;
    }
    return people.some(person => person.id === seed.id) ? people : [seed, ...people];
  }

  static limitCharacterLength(textToLimit: string, maxLength: number) {
    if (!textToLimit || !textToLimit?.length) {
      return textToLimit;
    }

    return textToLimit.length > maxLength ? textToLimit.slice(0, maxLength) : textToLimit;
  }

  static stringToNumber(value?: string | number): number {
    if (!value) {
      return 0
    }
    let stringValue = value.toString();
    const isNegative = stringValue.startsWith("-");

    let resp = Number(stringValue);
    return isNegative ? (resp * -1) : resp;
  }

  static getProvider(emailDomain: string): string {
    switch (emailDomain) {
      case 'hotmail':
      case 'outlook':
      case 'live':
        return 'http://outlook.live.com/default.aspx?rru=compose';
      case 'gmail':
        return 'https://mail.google.com/mail/?view=cm&fs=1';
      case 'yahoo':
        return 'https://us-mg5.mail.yahoo.com/neo/launch?action=compose';
      case 'vivaldi':
        return 'https://mail.vivaldi.net/webmail/?_task=mail&_action=compose';
      default:
        return 'mailto:'
    }
  }

  static transformTime(difference: number): string {
    if (difference <= 60) {
      return `${difference} segundo${difference === 1 ? '' : 's'} atrás`;
    } else if (difference <= 3600) {
      const minutes = Math.floor(difference / 60);
      return `${minutes} minuto${minutes === 1 ? '' : 's'} atrás`;
    } else if (difference <= 86400) {
      const hours = Math.floor(difference / 3600);
      return `${hours} hora${hours === 1 ? '' : 's'} atrás`;
    } else if (difference <= 2628000) {
      const days = Math.floor(difference / 86400);
      return `${days} dia${days === 1 ? '' : 's'} atrás`;
    } else {
      return "mais de um mês atrás";
    }
  }

  static sendEmail(from: string, to: string, subject: string, body: string) {
    from = ((from.split('@', 2)[1]).split('.')[0]);
    let provider = Utilities.getProvider(from);
    let strTo = (provider == 'yahoo') ? '&To=' : (provider == 'vivaldi') ? '&_to=' : '&to=';
    let strSubject = (provider == 'gmail') ? '&su=' : '&subject=';
    const href = `${provider}${strTo}${to}${strSubject}${subject}&body=${body}`
    window.open(href, '_blank')
  }

  /**
   * This will remove all non-numeric digits of a given string
   *
   * If you want just remove the special characters, call `removeSpecialCharacters()` or `removeDiacritcs()`
   */
  static removeMask(text: string | undefined): string {
    return String(text).replace(/\D/g, '');
  }

  static numeric(text: string): number {
    let value = Number(text.replace(/[^\d.,]/g, '').replace(/,/, '.'))
    return value
  }

  static removeDiacritics(text: string): string {
    if (!text || typeof text !== 'string' || !text?.length) {
      return "";
    }
    return text.normalize('NFD').replace(/[\u0300-\u036f\&]/g, '');
  }

  // remove ".", "-" and "/" characters
  static removeCharacters(inputText: string): string {
    if (!inputText || !inputText.length) return "";

    const cleanedText = inputText.replace(/[.\-\/]/g, '');
    return cleanedText;
  }

  static removeSpecialCharacters(text: string): string {
    if (typeof text !== 'string') return text;
    return text.normalize("NFD")
      .replace(/[\p{Diacritic}]/gu, '') // remove acents
      .replace(/[^\p{L}\p{N}\-\/,.:;_()%&+*?!@#$| ]/gu, '') // keep only allowed chars
      .replace(/\s+/g, ' ') // compact multiple spaces
      .trim();
  }

  static numberFieldsLengthIsInvalid(numberFieldValue: string, expectedLength: number, needToBeEqual = false): boolean {
    const numberFieldValueUnmasked = numberFieldValue.replace(/\D/g, "");
    return !needToBeEqual ? numberFieldValueUnmasked.length < expectedLength : numberFieldValueUnmasked.length !== expectedLength;
  }

  static getDiscount(gross: number, net: number): number {
    return (gross - net) * 100 / gross;
  }

  static getNetValue(gross: number, discount: number): number {
    return this.roundNumber(gross * (1 - discount / 100));
  }

  static roundNumber(value: number): number {
    return Math.round(value * 100) / 100;
  }

  static dateAndTime(date: Date, time: string): Date {
    date.setHours(Number(time.substring(0, 2)), Number(time.substring(3)), 0, 0);
    return date;
  }

  static fiscalDate(value) {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return new Date(value.year(), value.month(), value.date(), value.hour(), value.minute(), value.second(), value.millisecond());
    }

    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds(), value.getMilliseconds());
    }

    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
      return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4] ?? 0),
        Number(match[5] ?? 0),
        Number(match[6] ?? 0),
        0
      );
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  static fiscalDateKey(value) {
    const date = Utilities.fiscalDate(value);
    if (!date) {
      return null;
    }

    return (date.getFullYear() * 10000) + ((date.getMonth() + 1) * 100) + date.getDate();
  }

  static isFiscalDateBetween(value, start, end) {
    const valueKey = Utilities.fiscalDateKey(value);
    if (!valueKey) {
      return false;
    }

    const startKey = Utilities.fiscalDateKey(start);
    const endKey = Utilities.fiscalDateKey(end);

    return (!startKey || valueKey >= startKey) && (!endKey || valueKey <= endKey);
  }

  static isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // static getLanguage(): string {
  //   if (!localStorage['language']) {
  //     localStorage['language'] = this.language;
  //   }
  //   return localStorage['language'] || this.language;
  // }

  static getLanguage(): string {
    try {
      const language = localStorage.getItem('language')
      if (!language) {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('language', this.language)
        } else if (typeof sessionStorage !== 'undefined') {
          // Fallback to sessionStorage if localStorage is not supported
          sessionStorage.setItem('language', this.language)
        } else {
          // If neither localStorage nor sessionStorage is supported
          console.log('Web Storage is not supported in this environment.')
        }
      }
      return localStorage.getItem('language') || this.language
    } catch (error) {
      return this.language
    }

  }

  static getLanguageAndCountry(): string {
    for (const language of Object.keys(languages)) {
      if (languages[language].name == this.language) {
        return `${languages[language].name}-${languages[language].country}`;
      }
    }
    return null
  }

  /**
   * Validate a duplicated unique value
   * @param list The array with existent objects
   * @param field The property name of unique field
   * @returns null or a object containing the duplicated property;
   */
  public static unique<T>(currentValue: T[keyof T], list: T[], field?: keyof T, removeNonNumberCharactersToValidate = false): ValidatorFn {
    return (control: AbstractControl) => {
      for (let item of list) {
        let controlValue = control.value;

        if (typeof controlValue === "string") {
          controlValue = controlValue.trim();
        }

        if (removeNonNumberCharactersToValidate) {
          controlValue = `${controlValue}`.replaceAll(/\D/g, "");
        }

        if (!field && controlValue === item && currentValue !== controlValue) {
          return { duplicated: true };
        }
        if (item[field] === controlValue && controlValue !== currentValue) {
          return { duplicated: true };
        }
      }
      return null;
    };
  }

  /**
 * Validate if the control value match an value of another(s) passed field
 *
 * @param properties the properties to compare with the control
 * @returns null or the first repeated property as error
 */
  public static differentValues(properties: string[]): (AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!!control.parent && !!control.parent.value) {
        for (const property of properties) {
          if (control.value && control.value === control.parent.controls[property].value) {
            return { equal: property }
          }
        }
      }
      return null
    };
  }

  public static ncmValidator() {
    return (control: AbstractControl<number>): ValidationErrors | null => {
      const contorlValue = control.value;
      if (!contorlValue) {
        return null;
      }

      if (contorlValue.toString().length === 8 && Utilities.stringHaveOnlyEqualCharacters(control.value?.toString())) {
        control.markAsTouched();
        return { onlyEqualCharacters: true };
      }

      if ((contorlValue === 0) || (contorlValue.toString().length !== 8)) {
        control.markAsTouched();
        return { ncmInvalid: true };
      }

      return null;
    }
  }

  public static ncmFormater(value: number | string): string {
    if (!value) {
      return `${value ?? ""}`;
    }

    const valueAsString = `${value}`;

    return valueAsString.replace(/(\d{4})(\d{2})(\d{2})/, "\$1.\$2.\$3");
  }

  public static rangeValidator(fieldToCompareFormPath: string, currentControlShouldBe: "less" | "higher", canBeEqual = false) {
    return (control: AbstractControl<number | string>): ValidationErrors | null => {
      const parent = control.parent;
      const numberShouldBeLess = { numberShouldBeLess: true };
      const numberShouldBeHigh = { numberShouldBeHigh: true };
      let result: { numberShouldBeLess: boolean } | { numberShouldBeHigh: boolean };

      if (!parent) {
        return null;
      }

      const controlToCompare: AbstractControl<number | string> = parent.get(fieldToCompareFormPath);
      const currentControlValue = +control.value;
      const fieldToCompareValue = +controlToCompare?.value;

      if (!currentControlValue || !fieldToCompareValue) {
        return null;
      }

      if (currentControlShouldBe === "less") {
        const errors = controlToCompare.errors;
        delete errors?.["numberShouldBeHigh"];

        result = this._getRangeValidatorResult({
          canBeEqual,
          firstValue: currentControlValue,
          shouldBeLess: true,
          secondValue: fieldToCompareValue,
          error: numberShouldBeLess
        });

        if (result) {
          controlToCompare.setErrors({
            ...errors,
            ...numberShouldBeHigh
          });
          controlToCompare.markAsTouched();
        } else {
          if (errors && Object.keys(errors).length) {
            controlToCompare.setErrors({
              ...errors,
            });
          } else {
            controlToCompare.setErrors(null);
          }

          controlToCompare.markAsTouched();
        }

        return result;
      } else {
        const errors = controlToCompare.errors;
        delete errors?.["numberShouldBeLess"];

        result = this._getRangeValidatorResult({
          canBeEqual,
          firstValue: currentControlValue,
          shouldBeLess: false,
          secondValue: fieldToCompareValue,
          error: numberShouldBeHigh
        });

        if (result) {
          controlToCompare.setErrors({
            ...errors,
            ...numberShouldBeLess
          });
          controlToCompare.markAsTouched();
        } else {
          if (errors && Object.keys(errors).length) {
            controlToCompare.setErrors({
              ...errors,
            });
          } else {
            controlToCompare.setErrors(null);
          }

          controlToCompare.markAsTouched();
        }

        return result;
      }
    }
  }

  public static validateNcmRanges() {
    return (formParent: FormArray): ValidationErrors | null => {
      const ncmRanges: {
        index: number;
        ncmStart: number;
        ncmEnd: number;
      }[] = [];

      for (let [index, control] of formParent.controls.entries()) {
        if (!control) {
          continue;
        }
        const { ncmStart, ncmEnd } = control.getRawValue();

        if (!ncmEnd && !ncmStart) {
          continue;
        }

        for (const ncmRange of ncmRanges) {
          const ncmRangeStart = ncmRange.ncmStart;
          const ncmRangeEnd = ncmRange.ncmEnd;

          if (+ncmStart >= ncmRangeStart && +ncmStart <= ncmRangeEnd) {
            return { mixedNcmRanges: true };
          } else if (+ncmEnd >= ncmRangeStart && +ncmEnd <= ncmRangeEnd) {
            return { mixedNcmRanges: true };
          } else if (+ncmStart <= ncmRangeStart && +ncmEnd >= ncmRangeEnd) {
            return { mixedNcmRanges: true };
          }
        }

        ncmRanges.push({
          index,
          ncmEnd: +ncmEnd,
          ncmStart: +ncmStart
        });
      }

      return null
    }
  }

  public static validateBranchActivityRange() {
    return (formParent: FormArray): ValidationErrors | null => {
      const controlsByBranchActivity = new Map<BranchActivity, AbstractControl[]>();

      for (const control of formParent.controls) {
        if (!control) continue;

        const { branchActivity } = control.getRawValue();

        if (!branchActivity) continue;

        const controls = controlsByBranchActivity.get(branchActivity) ?? [];
        controls.push(control);
        controlsByBranchActivity.set(branchActivity, controls);
      }

      let hasDuplicates = false;

      for (const controls of controlsByBranchActivity.values()) {
        const duplicated = controls.length > 1;

        if (duplicated) {
          hasDuplicates = true;
        }

        for (const control of controls) {
          Utilities._flagDuplicatedBranchActivity(control, duplicated);
        }
      }

      return hasDuplicates ? { mixedBranchActivityRanges: true } : null;
    }
  }

  /**
   * Drops an unset `_id` from each NCM range instead of sending it as `null`.
   *
   * `createNcmInterval` holds `_id` in a control seeded with `undefined`, and `getRawValue`
   * reads that back as `null`. Sending `null` makes Mongoose persist `_id: null` rather than
   * generate one, so a range created through the form never gets a stable identity — and the
   * audit log, which keys `ncmRange` on `_id`, falls back to dumping the whole array.
   */
  public static stripUnsetNcmRangeIds(ncmRange: IRawNcmRange[]): void {
    for (const range of ncmRange ?? []) {
      if (range && !range._id) {
        delete range._id;
      }
    }
  }

  private static _flagDuplicatedBranchActivity(rangeControl: AbstractControl, duplicated: boolean) {
    const branchActivityControl = rangeControl.get('branchActivity');

    if (!branchActivityControl) return;

    const errors = { ...(branchActivityControl.errors ?? {}) };
    const alreadyFlagged = !!errors['duplicatedBranchActivity'];

    if (duplicated === alreadyFlagged) return;

    if (duplicated) {
      errors['duplicatedBranchActivity'] = true;
    } else {
      delete errors['duplicatedBranchActivity'];
    }

    branchActivityControl.setErrors(Object.keys(errors).length ? errors : null, { emitEvent: false });
  }

  private static _getRangeValidatorResult({ firstValue, secondValue, shouldBeLess, canBeEqual, error }: {
    firstValue: number;
    secondValue: number;
    shouldBeLess: boolean
    canBeEqual: boolean;
    error: any;
  }) {
    if (shouldBeLess) {
      if (canBeEqual) {
        return firstValue > secondValue ? error : null;
      } else {
        return firstValue >= secondValue ? error : null;
      }
    } else {
      if (canBeEqual) {
        return firstValue < secondValue ? error : null;
      } else {
        return firstValue <= secondValue ? error : null;
      }
    }
  }

  public static setOtherFieldsRequired(fieldsThatShouldBeRequiredPaths: string[]) {
    return (control: AbstractControl<boolean>): ValidationErrors | null => {
      const parent = control.parent;

      if (!parent || !fieldsThatShouldBeRequiredPaths) {
        return null;
      }

      const isChecked = control.value;

      for (const fieldPath of fieldsThatShouldBeRequiredPaths) {
        const field = parent.get(fieldPath);

        if (!field) {
          continue;
        }

        if (isChecked) {
          field.addValidators([Validators.required]);
        } else {
          field.removeValidators(Validators.required);
        }

        field.updateValueAndValidity({ emitEvent: false, onlySelf: true });
      }

      return null;
    }
  }

  public static stringHaveOnlyEqualCharacters(text: string): boolean {
    if (!text) {
      return false;
    }
    const firstCharacter = text[0];
    return text.split("").every(character => character === firstCharacter);
  }

  /**
   *
   * @param value
   * @param maxWeigth
   * @param dvLength
   * @param minWeigth
   * @returns
   */
  public static validateDigit(value: string, maxWeigth = 9, dvLength = 2, minWeigth = 2): boolean {
    const PRIME_NUMBER_DV = 11;
    const numberArr = value.replace(/\D/g, '').split('').map(Number);
    let digitsArr = numberArr.splice(numberArr.length - dvLength, dvLength);

    while (digitsArr.length) {
      const digit = digitsArr.splice(0, 1)[0];
      let sum = 0;
      for (let i = 0; i < numberArr.length; i++) {
        let weight;
        if (maxWeigth) {
          weight = ((numberArr.length - i - 1) % (maxWeigth - 1)) + minWeigth;
        }
        sum += numberArr[i] * weight;
      }
      let validDigit = PRIME_NUMBER_DV - (sum % PRIME_NUMBER_DV);
      validDigit = (validDigit > 9 ? 0 : validDigit);

      if (validDigit !== digit) {
        // invalid
        return false
      }
      numberArr.push(digit);
    }

    // valid
    return true;
  }

  /**********************************************************
   * @param value
   * @param maxWeigth
   * @param dvLength
   * @param minWeigth
   * @returns
   *
   * Conversão de letras:
   * - Letras maiúsculas são convertidas com base na tabela ASCII:
   * - Valor = ASCII(letra) - 48
   * - Exemplo: A = 65 → 65 - 48 = 17, B = 66 → 18, C = 67 → 19, etc.
   *
   *
   * Cálculo do primeiro dígito verificador (DV1):
   * ---------------------------------------------
   * Exemplo CNPJ: 1 2 A  B  C   3  4  5  0 1 D  E - DV
   * Valores:      1 2 17 18 19  3  4  5  0 1 20 21
   * Pesos:        5 4  3  2  9   8  7  6  5 4  3  2
   * Multiplica:   5 8 51 36 171 24 28 30 0 4 60 42
   *
   * Soma: 459
   * Resto da divisão por 11: 459 % 11 = 8
   *
   * DV1 = (resto < 2) ? 0 : 11 - resto → DV1 = 3
   *
   * Cálculo do segundo dígito verificador (DV2):
   * --------------------------------------------
   * Acrescenta DV1 ao final: ... E 3
   * Valores:      1 2 17 18 19  3  4  5  0 1 20 21 3
   * Pesos:        6 5  4  3  2   9  8  7  6 5  4  3 2
   * Multiplica:   6 10 68 54 38 27 32 35 0 5 80 63 6
   * Soma: 424
   * Resto da divisão por 11: 424 % 11 = 6
   * DV2 = (resto < 2) ? 0 : 11 - resto → DV2 = 5
   *
   * Resultado final: 12.ABC.345/01DE-35
   ********************************************************/
  public static validateDigitNewFormatCnpj(value: string, maxWeigth = 9, dvLength = 2, minWeigth = 2): boolean {
    const charToNum = (c: string): number => {
      return /\d/.test(c) ? parseInt(c, 10) : c.charCodeAt(0) - 48;
    };

    const numbers = value
      .toUpperCase()
      .split('')
      .map(charToNum);

    const baseLength = numbers.length - dvLength;

    for (let dvIndex = 0; dvIndex < dvLength; dvIndex++) {
      const calcArr = numbers.slice(0, baseLength + dvIndex).reverse();
      let soma = 0;
      let peso = minWeigth;

      for (const d of calcArr) {
        soma += peso * d;
        peso++;
        if (peso > maxWeigth) peso = minWeigth; // reinicia o peso quando passar do max
      }

      const resto = soma % 11;
      const dv = resto < 2 ? 0 : 11 - resto;
      if (dv !== numbers[baseLength + dvIndex]) return false;
    }

    return true;
  }


  /**
   * @param validateEquals Validate if all digits are equals
   * @param validateDigit Validate with verifying digit
   */
  static validateCPF(validateEquals = true, validateDigit = true, validateLength?: boolean) {
    const CPF_LENGTH = 11;
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value.replace(/\D/g, '');

      // Verify string length.
      if (validateLength && value.length !== CPF_LENGTH) {
        return { length: true };
      }

      if (validateEquals && (/^([0-9])\1*$/.test(value))) {
        return { equalDigits: true };
      }

      if (validateDigit && !Utilities.validateDigit(value, 12)) {
        return { digit: true }
      }

      return null;
    }
  }


  /**
   * Formato do novo CNPJ alfanumérico:
   * -----------------------------------
   *   SS.SSS.SSS / SSSS - NN
   *
   * Onde:
   *   - S: Letra ou número
   *   - N: Apenas número
   *
   * @param validateEquals Default true: Validate if all digits are equals
   * @param validateDigit Default true: Validate with verifying digit
   *
   **/
  static validateCNPJ(validateEquals = true, validateDigit = true, validateLength?: boolean) {
    const CNPJ_LENGTH = 14;
    return (control: AbstractControl): ValidationErrors | null => {
      // Remove apenas . / - e deixa números e letras
      const raw = (control.value || '')
        .toString()
        .toUpperCase()
        .replace(/[\.\-\/]/g, '');

      if (validateLength && raw.length !== CNPJ_LENGTH) {
        return { length: true };
      }

      if (validateEquals && /^([A-Z0-9])\1*$/.test(raw)) {
        return { equalDigits: true };
      }

      if (validateDigit && raw.length === CNPJ_LENGTH && !Utilities.validateDigit(raw)) {
        return { digit: true };
      }

      return null;
    };
  }


  static checkIfOnlyWhiteSpaces(): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const controlValue = control.value

      if (typeof controlValue !== "string") {
        return null;
      }

      if (!controlValue.trim()) {
        return { onlyWhiteSpaces: true };
      }


      return null;
    }
  }

  /**
   * This validator set the both fields to required if one of the two field is filled
   * 
   * Use when you need that one field complements another
   */
  static requiredIfOneIsFilled(otherFieldName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const parentField = control.parent;

      if (!parentField) {
        return null;
      }

      const otherField = parentField.get(otherFieldName);

      if ((otherField.value && !control.value) || (!otherField.value && control.value)) {
        otherField.setErrors({ bothDatesRequired: true });
        otherField.markAsTouched();

        return { bothDatesRequired: true };
      }
      otherField.setErrors(null);

      return null;
    }
  }

  static ValidatePhoneFieldsValue(onlyOneRequired = true) {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      if (!control.parent) return null;

      const phone1: AbstractControl<string> = control.parent.get("phone1");
      const phone2: AbstractControl<string> = control.parent.get("phone2");

      const phone1Value = phone1?.value?.trim() ?? "";
      const phone2Value = phone2?.value?.trim() ?? "";

      if (!phone2Value.length && !phone2Value.length) {
        if (!phone1.hasValidator(Validators.required)) {
          phone1.addValidators(Validators.required);
        }

        if (!phone2.hasValidator(Validators.required)) {
          phone2.addValidators(Validators.required);
        }
      }

      if (onlyOneRequired && (phone1Value.length > 0 || phone2Value.length > 0)) {
        if (phone1Value.length && !phone2Value.length) {
          if (!phone1.hasValidator(Validators.required)) {
            phone1.addValidators(Validators.required);
          }

          if (phone2.hasValidator(Validators.required)) {
            phone2.removeValidators(Validators.required);
            phone2.markAsTouched();
            phone2.setErrors(null);
          }
        }

        if (phone2Value.length && !phone1Value.length) {
          if (!phone2.hasValidator(Validators.required)) {
            phone2.addValidators(Validators.required);
          }

          if (phone1.hasValidator(Validators.required)) {
            phone1.removeValidators(Validators.required);
            phone1.markAsTouched();
            phone1.setErrors(null);
          }
        }

        return null;
      }

      return { onePhoneRequired: true };
    }
  }

  static distinctControls(controlName1: string, controlName2: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value1 = control.get(controlName1)?.value;
      const value2 = control.get(controlName2)?.value;

      if (value1 && value2 && value1 === value2) {
        return { controlsEquals: true };
      }

      return null;
    };
  }


  static toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise(resolve => canvas.toBlob(blob => resolve(blob), 'image/jpeg'));
  }

  static moveCaretToEnd(element: HTMLTableCellElement) {
    const range = new Range();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  static getNumber(text: string, digits = Number.POSITIVE_INFINITY): number {
    return Number(this.removeMask(text.toString()).substring(0, digits)) / 100;
  }

  /**
 * Calculate a percentage of a number from other number
 *
 * @param percentage the percentage to calculate
 * @param total the number equivalent to 100%
 * @param mode round or trunc the decimal points.
 *
 * Use 'round' when work with discounts (total - x%)
 *
 * Use 'trunc' when work with the percentage value (x% of total)
 *
 * @example
 * // 7% of 107.5 is 7.525
 *
 * // to calculate 7% of 107.50
 * const v = calcPercentage(7, 107.5); // return 7.53
 *
 * // to reduce 7% from 107.50
 * const v = calcPercentage(7, 107.5, "round"); // 99.98
 * const discount = 107.5 - resp; // return 7.52
 *
 * // to get real value
 * const v = calcPercentage(7, 107.5, "trunc", 3); // return 7.525
 *
 */
  static calcPercentage(percentage: number, total: number, mode?: 'trunc' | 'round', decimalPoints = 2): number {
    if (!percentage || !total) {
      // prevent NaN
      return 0
    }

    switch (mode) {
      case 'round':
        const exp = Math.pow(10, decimalPoints);
        const discount = Math.round((exp * (100 - percentage) * (total / 100))) / exp;
        return Number((total - discount).toFixed(decimalPoints)) || 0;
      default:
        return Math.floor(total * percentage) / 100
    }
  }

  /**
   * Calculate the corresponding percentage from two values
   * @param value the fraction value
   * @param total this value represent 100%
   * @example
   * ```
   * // calculate how percents is 30 into 120
   * getPercentage(30, 120); // return 25.00
   * ```
   */
  static getPercentage(value: number, total: number, decimalPoints = 2) {
    if (!total) {
      return 0;
    }
    return Number((value * 100 / total).toFixed(decimalPoints));
  }

  /**
   * Insert a mask into object property
   *
   * @param value The number or string representing value
   * @param decimalConfig The min and max decimal points, default is { min: 2, max: 2 }
   *
   * @example
   *
   * ```
   *  const value = 30;
   *  const masked = Utilities.getMaskedNumberOf(value);
   *  console.log(masked); // '30,00'
   * ```
   */
  static getMaskedNumberOf(value: any, decimalConfig?: { min: number, max: number }): string {
    if (['', undefined, null].includes(value)) {
      return '';
    }
    const newValue = typeof value === 'string' ? Number(value) : value;
    const decimalString = decimalConfig ? `1.${decimalConfig.min}-${decimalConfig.max}` : '1.2-2';
    const maskedValue: any = new DecimalPipe('pt-BR').transform(newValue, decimalString);
    return maskedValue;
  }

  protected static equalNumberWithDefault(a: number, b: number, defaultValue: number): boolean {
    return (a == null && b == defaultValue) || a == b;
  }

  protected static equalString(a: string | undefined, b: string | undefined): boolean {
    return (!a && !b) || a == b;
  }

  public static equalUnmasked(a: string | undefined, b: string | undefined): boolean {
    return this.removeMask(a) == this.removeMask(b);
  }

  protected static equalDate(a: string, b: string): boolean {
    if (!a) {
      a = null;
    }
    if (!b) {
      b = null;
    }
    return new Date(a).getTime() == new Date(b).getTime();
  }

  /** Return a new object without NaN, null or undefined values */
  static removeNull(object: Object, recursive = false): any {
    let resp = structuredClone(object);
    for (const key in resp) {
      const value = resp[key];
      if (recursive) {
        if (Array.isArray(value)) {
          resp[key] = resp[key].map(item => Utilities.removeNull(item, true)).filter(item => item !== null);
        } else if (typeof value == "object") {
          resp[key] = Utilities.removeNull(resp[key], true);
        }
      }
      if (Number.isNaN(value) || [null, undefined].includes(value)) {
        delete resp[key];
      }

      // remove empty objects
      if (value && (typeof value == 'object') && !Object.keys(resp[key]).length) {
        delete resp[key];
      }
    }
    return resp;
  }

  /** Sort data of array acording most recent dates into `updatedAt` field */
  static sortByUpdatedAt(arr: any[]) {
    arr.sort((a, b) => {
      if (a.updatedAt && b.updatedAt) {
        return new Date(b.updatedAt).getTime() < new Date(a.updatedAt).getTime() ? -1 : 1;
      }
      return 1;
    })
  }

  static currencyFormat(value: number): string {
    return value.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      style: 'currency',
      currency: 'BRL'
    });
  }

  static getRangeString(date1: string | Date | Moment, date2: string | Date | Moment) {
    const startDate = isMoment(date1) ? date1.toDate() : new Date(date1);
    const endDate = isMoment(date2) ? date2.toDate() : new Date(date2);
    let rangeString = "";
    if (startDate.valueOf() && endDate.valueOf()) {
      rangeString = `(entre ${startDate.toLocaleDateString()} e ${endDate.toLocaleDateString()})`;
    } else if (Boolean(startDate.valueOf()) !== Boolean(endDate.valueOf())) {
      if (startDate.valueOf()) {
        rangeString = `(após ${startDate.toLocaleDateString()})`;
      } else {
        rangeString = `(até ${endDate.toLocaleDateString()})`;
      }
    }
    return rangeString;
  }

  /**
   * Sum the numeric value of each object in a provided array
   * and change some value if the sum is different of expected value
   *
   * @param array the array of objects.
   * @param property the name of property to be calculated - need to be numeric.
   * @param expectedValue the sum expected.
   * @optional
   * @param distributive if false(default) adjust only the first item of array, otherwise
   * interate the array and sum or subtract 1 unit of precision until the sum be equal the expected value.
   * @param decimalPoints The precision to fix, 2 by default (for currency).
   * @returns void, but modify the original array.
   */
  static fixCentsDifference<T extends object>(array: Array<T>, accessorConfig: string | {
    property: string,
    accessor?: (item: T) => object,
  }, expectedValue: number, distributive = false, decimalPoints = 2): void {
    let property: string, accessor: (item: T) => object;

    if (typeof accessorConfig === "string") {
      property = accessorConfig;
    } else {
      property = accessorConfig.property;
      accessor = accessorConfig.accessor;
    }

    if (!Array.isArray(array)) {
      console.warn(`Invalid array`);
      return;
    }

    if (!array.length) {
      console.warn(`Empty array`, array);
      return;
    }

    if (!property) {
      console.warn(`Invalid property`);
      return;
    }

    if (!Number.isFinite(expectedValue)) {
      console.warn("Invalid value for parameter 'expectedValue':", expectedValue);
      return;
    }

    let invalidItem = !accessor ?
      array.find((item) => !Object.hasOwn(item, property)) :
      array.find((item) => !Object.hasOwn(accessor(item), property));

    if (invalidItem) {
      console.warn(`Item without property "${property as string}"`, invalidItem);
      return;
    }

    invalidItem = !accessor ?
      array.find(item => typeof item[property] !== "number") :
      array.find(item => typeof accessor(item)[property] !== "number");

    if (invalidItem) {
      console.warn(`Item with invalid property "${property as string}"`, invalidItem);
      return;
    }

    const precision = Math.pow(10, (decimalPoints * -1));
    const sum = !accessor ?
      Number((array.reduce((acc, item) => acc + (item[property] as number), 0).toFixed(decimalPoints))) :
      Number((array.reduce((acc, item) => acc + (accessor(item)[property] as number), 0).toFixed(decimalPoints)));
    let difference = Number((expectedValue - sum).toFixed(decimalPoints));

    if (distributive) {
      let correctionIndex = 0; // start fixing by first item
      while (Math.abs(difference) >= precision) {
        if (difference > 0) {
          if (!accessor) {
            array[correctionIndex][property] = Number(((array[correctionIndex][property] as number) + precision).toFixed(decimalPoints)) as any;
          } else {
            accessor(array[correctionIndex])[property] = Number(((accessor(array[correctionIndex])[property] as number) + precision).toFixed(decimalPoints)) as any;
          }

          difference -= precision;
        } else {
          if (!accessor) {
            array[correctionIndex][property] = Number(((array[correctionIndex][property] as number) - precision).toFixed(decimalPoints)) as any;
          } else {
            accessor(array[correctionIndex])[property] = Number(((accessor(array[correctionIndex])[property] as number) - precision).toFixed(decimalPoints)) as any;
          }

          difference += precision;
        }
        correctionIndex = (correctionIndex + 1) % array.length;
      }
    } else if (Math.abs(difference) >= precision) {
      if (!accessor) {
        array[0][property] = Number(((array[0][property] as number) + difference).toFixed(decimalPoints)) as any;
      } else {
        accessor(array[0])[property] = Number(((array[0][property] as number) + difference).toFixed(decimalPoints)) as any;
      }
    }
  }

  static removeMongoProperties(apiObject: any, deleteID = true): object {
    const copy = { ...apiObject };
    delete copy.updatedAt;
    delete copy.createdAt;
    delete copy.company;
    delete copy.seq;
    delete copy.__v;
    if (deleteID) {
      delete copy._id;
    }
    return copy;
  }

  /**
   * Calcula quanto tempo (em milissegundos) foi contabilizado dentro do período
   * do dia em que o cronômetro foi iniciado.
   *
   * @param startTime - dia/hora (em ms) de quando o cronômetro foi iniciado
   * @param endTime - dia/hora (em ms) de quando o cronômetro foi pausado
   * @returns número de milissegundos contabilizados no intervalo (startTime até às min(endTime, 18:00))
   * do dia do início do apontamento
   */
  static countTimeUntilWorkdayEnd(startTime: number, endTime: number): number {
    // data de inicio do serviço
    const startDate = new Date(startTime);
    const endOfWorkday = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      18, 0, 0, 0
    ).getTime();

    // se foi pausado depois das 18 do dia iniciado, contar somente até Às 18 do dia iniciado
    const effectiveEnd = Math.min(endTime, endOfWorkday);

    // contabiliza somente o tempo valido
    return Math.max(0, effectiveEnd - startTime);
  }

  static focusFirstInvalidField(form: FormGroup): void {
    const formControls = form.controls;
    for (const controlName in formControls) {
      const control = formControls[controlName];
      if (control.errors) {
        const inputElement = document.querySelector(`[formControlName="${controlName}"]`) as HTMLElement;
        if (inputElement) {
          inputElement.focus();
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    }
  }

  static parseCodes(codeString: string): string {
    const codes = codeString.match(/\d+/g) || [];

    const uniqueCodes = [...new Set(codes)];
    return uniqueCodes.join(this.defaultSeparator);
  }

  static createDate(date: any): Date | null {
    if (!date) return null;

    if (date && typeof date === 'object' && typeof date.format === 'function') {
      const formatted = date.format('YYYY-MM-DD');
      const [year, month, day] = formatted.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    if (typeof date === 'string') {
      const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
    }

    if (date instanceof Date) {
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      return new Date(year, month, day);
    }

    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      const d = date.toDate();
      const year = d.getUTCFullYear();
      const month = d.getUTCMonth();
      const day = d.getUTCDate();
      return new Date(year, month, day);
    }

    console.warn('createDate: tipo de data inválido', date, typeof date);
    return null;
  }

  static getSaudation(): string {
    const currentHour = new Date().getHours();
    if (currentHour >= 5 && currentHour < 12) {
      return 'SAUDATION.MORNING';
    } else if (currentHour >= 12 && currentHour < 18) {
      return 'SAUDATION.AFTERNOON';
    } else {
      return 'SAUDATION.EVENING';
    }
  }
}
